import { formatSubscriptionDate } from './businessSubscriptionsData';

export const SUBSCRIPTION_HISTORY_EVENTS = [
  'Trial Started',
  'Trial Converted',
  'Trial Expired',
  'Subscription Activated',
  'Subscription Inactivated',
  'Upgrade Applied',
  'Downgrade Scheduled',
  'Downgrade Withdrawn',
  'Downgrade Applied',
  'Cancellation Scheduled',
  'Cancellation Withdrawn',
  'Cancellation Applied',
  'Grace Period Started',
  'Grace Period Ended',
  'Subscription Reactivated',
];

export const SUBSCRIPTION_HISTORY_SOURCES = ['Business', 'System', 'Stripe'];

export const SUBSCRIPTION_HISTORY_OUTCOMES = ['Scheduled', 'Applied', 'Withdrawn', 'Failed'];

/**
 * @typedef {Object} SubscriptionHistoryRecord
 * @property {string} id
 * @property {string} businessId
 * @property {string} event
 * @property {string} from
 * @property {string} to
 * @property {'Business'|'System'|'Stripe'} source
 * @property {string|null} requestedAt ISO date
 * @property {string|null} effectiveAt ISO date
 * @property {'Scheduled'|'Applied'|'Withdrawn'|'Failed'} outcome
 */

/** @type {SubscriptionHistoryRecord[]} */
const allSubscriptionHistory = [
  // Werribee Plumbing — sample set from specs (+ activation path)
  {
    id: 'HIST-000901',
    businessId: 'BUS-9934',
    event: 'Trial Started',
    from: '—',
    to: 'Basic · Trial',
    source: 'Business',
    requestedAt: '2024-03-12',
    effectiveAt: '2024-03-12',
    outcome: 'Applied',
  },
  {
    id: 'HIST-000902',
    businessId: 'BUS-9934',
    event: 'Upgrade Applied',
    from: 'Basic · Trial',
    to: 'Pro · Active',
    source: 'Business',
    requestedAt: '2024-03-20',
    effectiveAt: '2024-03-20',
    outcome: 'Applied',
  },
  {
    id: 'HIST-001274',
    businessId: 'BUS-9934',
    event: 'Cancellation Scheduled',
    from: 'Pro · Active',
    to: 'Cancellation · 07 Jun 2026',
    source: 'Business',
    requestedAt: '2026-05-10',
    effectiveAt: '2026-06-07',
    outcome: 'Withdrawn',
  },
  {
    id: 'HIST-001275',
    businessId: 'BUS-9934',
    event: 'Cancellation Withdrawn',
    from: 'Cancellation Scheduled',
    to: 'None',
    source: 'Business',
    requestedAt: '2026-05-12',
    effectiveAt: '2026-05-12',
    outcome: 'Applied',
  },

  // Apex Drainage — Basic trial in progress
  {
    id: 'HIST-002100',
    businessId: 'BUS-7142',
    event: 'Trial Started',
    from: '—',
    to: 'Basic · Trial',
    source: 'Business',
    requestedAt: '2026-07-20',
    effectiveAt: '2026-07-20',
    outcome: 'Applied',
  },

  // Urban Tree — grace period after failed renewal
  {
    id: 'HIST-003010',
    businessId: 'BUS-3625',
    event: 'Trial Started',
    from: '—',
    to: 'Basic · Trial',
    source: 'Business',
    requestedAt: '2025-10-15',
    effectiveAt: '2025-10-15',
    outcome: 'Applied',
  },
  {
    id: 'HIST-003011',
    businessId: 'BUS-3625',
    event: 'Trial Converted',
    from: 'Basic · Trial',
    to: 'Basic · Active',
    source: 'Stripe',
    requestedAt: '2025-11-12',
    effectiveAt: '2025-11-12',
    outcome: 'Applied',
  },
  {
    id: 'HIST-003200',
    businessId: 'BUS-3625',
    event: 'Grace Period Started',
    from: 'Basic · Active',
    to: 'Basic · Grace Period',
    source: 'System',
    requestedAt: '2026-08-26',
    effectiveAt: '2026-08-26',
    outcome: 'Applied',
  },

  // Northside Electrical — cancellation scheduled (still active)
  {
    id: 'HIST-004001',
    businessId: 'BUS-5810',
    event: 'Subscription Activated',
    from: '—',
    to: 'Pro · Active',
    source: 'Business',
    requestedAt: '2024-06-01',
    effectiveAt: '2024-06-01',
    outcome: 'Applied',
  },
  {
    id: 'HIST-004180',
    businessId: 'BUS-5810',
    event: 'Cancellation Scheduled',
    from: 'Pro · Active',
    to: 'Cancellation · 01 Sep 2026',
    source: 'Business',
    requestedAt: '2026-08-10',
    effectiveAt: '2026-09-01',
    outcome: 'Scheduled',
  },

  // Rapid Roofing — inactivated after failed payment
  {
    id: 'HIST-005001',
    businessId: 'BUS-4478',
    event: 'Trial Started',
    from: '—',
    to: 'Basic · Trial',
    source: 'Business',
    requestedAt: '2025-01-17',
    effectiveAt: '2025-01-17',
    outcome: 'Applied',
  },
  {
    id: 'HIST-005002',
    businessId: 'BUS-4478',
    event: 'Trial Converted',
    from: 'Basic · Trial',
    to: 'Basic · Active',
    source: 'Stripe',
    requestedAt: '2025-02-14',
    effectiveAt: '2025-02-14',
    outcome: 'Applied',
  },
  {
    id: 'HIST-005100',
    businessId: 'BUS-4478',
    event: 'Grace Period Started',
    from: 'Basic · Active',
    to: 'Basic · Grace Period',
    source: 'System',
    requestedAt: '2026-07-21',
    effectiveAt: '2026-07-21',
    outcome: 'Applied',
  },
  {
    id: 'HIST-005101',
    businessId: 'BUS-4478',
    event: 'Grace Period Ended',
    from: 'Basic · Grace Period',
    to: 'Basic · Inactive',
    source: 'System',
    requestedAt: '2026-07-28',
    effectiveAt: '2026-07-28',
    outcome: 'Applied',
  },
  {
    id: 'HIST-005102',
    businessId: 'BUS-4478',
    event: 'Subscription Inactivated',
    from: 'Basic · Grace Period',
    to: 'Basic · Inactive',
    source: 'System',
    requestedAt: '2026-07-28',
    effectiveAt: '2026-07-28',
    outcome: 'Applied',
  },
];

export function getSubscriptionHistoryForBusiness(businessId) {
  if (!businessId) return [];
  const id = String(businessId).trim().toLowerCase();
  return allSubscriptionHistory
    .filter((row) => String(row.businessId).toLowerCase() === id)
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a.requestedAt || a.effectiveAt || 0).getTime();
      const bTime = new Date(b.requestedAt || b.effectiveAt || 0).getTime();
      return bTime - aTime;
    });
}

export function formatHistoryDate(isoDate) {
  return formatSubscriptionDate(isoDate);
}
